/**
 * /gift/redeem/:code — Recipient enters their code (or arrives via deep
 * link from the email), creates/logs in, and we provision the sub.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GiftIcon as Gift, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { formatApiError } from "@/lib/utils-app";

export default function GiftRedeem() {
  const { code: paramCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [code, setCode] = useState(paramCode || "");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);

  useEffect(() => {
    if (paramCode && !isNativeApp()) {
      lookup(paramCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramCode]);

  const lookup = async (c) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gift/lookup/${c.toUpperCase().trim()}`);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const redeem = async () => {
    if (!user) {
      navigate(`/login?next=/gift/redeem/${code.toUpperCase().trim()}`);
      return;
    }
    setRedeeming(true);
    try {
      const { data } = await api.post(`/me/gift/redeem/${code.toUpperCase().trim()}`);
      setRedeemed(data);
      toast.success("Subscription activated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRedeeming(false);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  // App Review 3.1.1: gift-code redemption unlocks digital content —
  // not permitted inside the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return (
    <Shell>
      <div className="max-w-xl mx-auto px-4 py-12" data-testid="gift-redeem-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Redeem a gift</p>
        <h1 className="font-spectral font-light text-4xl tracking-tight leading-[1.05] flex items-center gap-3">
          <Gift size={28} weight="duotone" className="text-gold"/>
          You've got a gift.
        </h1>

        {redeemed ? (
          <div className="mt-8 rounded-xl border border-[#0F8043]/40 bg-[#EFF7EE] p-5 flex items-start gap-3" data-testid="gift-redeemed">
            <CheckCircle size={20} weight="fill" className="text-[#0F8043] flex-shrink-0"/>
            <div className="text-[13.5px]">
              <p className="font-semibold text-[#0F8043]">Redeemed!</p>
              <p className="text-[#3F3F3F] mt-1 leading-relaxed">
                Your <strong>{redeemed.plan}</strong> subscription is active until <strong>{new Date(redeemed.expires_at).toLocaleDateString()}</strong>.
              </p>
              <Link to="/dashboard" className="inline-block mt-3 text-crimson underline font-bold text-[12px]">Go to my dashboard →</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-8 space-y-3">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                className="font-tabular text-lg tracking-widest text-center h-14"
                data-testid="gift-redeem-code-input"
              />
              <Button
                onClick={() => lookup(code)}
                disabled={loading || !code.trim()}
                variant="outline"
                className="w-full rounded-full h-11"
                data-testid="gift-redeem-lookup"
              >
                {loading ? "Looking up…" : "Look up gift"}
              </Button>
            </div>

            {preview && (
              <div className="mt-8 rounded-xl border-2 border-[#C9A227] bg-[#FBF7EC] p-5" data-testid="gift-preview">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] mb-2">Gift preview</p>
                <p className="font-spectral text-2xl tracking-tight">{preview.plan_name}</p>
                <p className="text-[13px] text-[#3F3F3F] mt-1">
                  For <strong>{preview.recipient_name}</strong>
                  {preview.sender_name && <> from <strong>{preview.sender_name}</strong></>}
                </p>
                {preview.note && (
                  <blockquote className="border-l-2 border-[#C9A227] pl-3 mt-3 text-[13px] text-[#6B6B6B] italic whitespace-pre-wrap">
                    {preview.note}
                  </blockquote>
                )}
                <Button
                  onClick={redeem}
                  disabled={redeeming}
                  className="mt-5 w-full rounded-full bg-ink hover:bg-crimson text-white h-11"
                  data-testid="gift-redeem-confirm"
                >
                  {redeeming ? "Redeeming…" : user ? "Redeem to my account" : "Sign in & redeem"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}
